import * as React from "react";
import { useAuthContext } from "@/contexts/AuthContext";
import { notificationRepository } from "@/data/repositories";
import type { Notification } from "@/types/domain/collaboration";

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const NotificationContext = React.createContext<NotificationContextValue | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuthContext();
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;
    if (!user) {
      setNotifications([]);
      return undefined;
    }
    setIsLoading(true);
    setError(null);
    notificationRepository.listForUser(user.id)
      .then((items) => active && setNotifications(items))
      .catch((cause: unknown) => active && setError(cause instanceof Error ? cause.message : "Could not load notifications."))
      .finally(() => active && setIsLoading(false));
    return () => { active = false; };
  }, [user]);

  const markRead = React.useCallback(async (id: string) => {
    const updated = await notificationRepository.markRead(id);
    setNotifications((items) => items.map((item) => item.id === id ? updated : item));
  }, []);

  const markAllRead = React.useCallback(async () => {
    if (!user) return;
    await notificationRepository.markAllRead(user.id);
    const readAt = new Date().toISOString();
    setNotifications((items) => items.map((item) => item.readAt ? item : { ...item, readAt }));
  }, [user]);

  const value = React.useMemo(() => ({
    notifications,
    unreadCount: notifications.filter((item) => !item.readAt).length,
    isLoading,
    error,
    markRead,
    markAllRead,
  }), [notifications, isLoading, error, markRead, markAllRead]);

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const context = React.useContext(NotificationContext);
  if (!context) throw new Error("useNotifications must be used within a NotificationProvider");
  return context;
}
